import { useEffect, useRef, useState } from "react";
import { useMotionPolicy } from "../../motion/useMotionPolicy";

const DESKTOP_END_POSTER =
  "/media/higgsfield/sweet-impact-master-desktop-poster.avif?v=20260801-baked";
const DESKTOP_START_POSTER =
  "/media/higgsfield/sweet-impact-master-desktop-start.avif?v=20260801-baked";
const MOBILE_POSTER =
  "/media/higgsfield/sweet-impact-mobile.avif?v=20260801-baked";
const FALLBACK_POSTER = "/products/hero-freeze-chamber.png";

function safelyPlay(video: HTMLVideoElement) {
  try {
    void video.play().catch(() => {});
  } catch {
    // The poster remains visible when a browser rejects playback synchronously.
  }
}

function readDesktopViewport() {
  return typeof window !== "undefined" && window.innerWidth > 700;
}

function useDesktopViewport() {
  const [isDesktop, setIsDesktop] = useState(readDesktopViewport);

  useEffect(() => {
    const query = window.matchMedia("(min-width: 701px)");
    const updateViewport = () => setIsDesktop(query.matches);

    if (typeof query.addEventListener === "function") {
      query.addEventListener("change", updateViewport);
      return () => query.removeEventListener("change", updateViewport);
    }

    query.addListener(updateViewport);
    return () => query.removeListener(updateViewport);
  }, []);

  return isDesktop;
}

export function CampaignImpactMedia() {
  const tier = useMotionPolicy();
  const isDesktop = useDesktopViewport();
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasEndedRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [posterFailed, setPosterFailed] = useState(false);
  const allowsVideo =
    isDesktop && (tier === "medium" || tier === "high");
  const advertisedReady = allowsVideo && ready;

  useEffect(() => {
    if (allowsVideo) {
      hasEndedRef.current = false;
    } else {
      setReady(false);
    }
  }, [allowsVideo]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !allowsVideo || typeof IntersectionObserver === "undefined") {
      return;
    }

    let isIntersecting = false;
    const updatePlayback = () => {
      if (isIntersecting && !document.hidden && !hasEndedRef.current) {
        safelyPlay(video);
      } else {
        video.pause();
      }
    };
    const observer = new IntersectionObserver(([entry]) => {
      isIntersecting = Boolean(entry?.isIntersecting);
      updatePlayback();
    });

    observer.observe(video);
    document.addEventListener("visibilitychange", updatePlayback);

    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", updatePlayback);
      video.pause();
    };
  }, [allowsVideo]);

  return (
    <div
      className="lumina-portal"
      data-branding="baked"
      data-ready={advertisedReady}
      data-treat-show-lumina
      data-sweet-impact
      data-mode={allowsVideo ? "video" : "static"}
      data-viewport={isDesktop ? "desktop" : "mobile"}
    >
      <picture className="lumina-portal__poster">
        {!posterFailed ? (
          <source
            media="(max-width: 700px)"
            srcSet={MOBILE_POSTER}
          />
        ) : null}
        <img
          src={
            posterFailed
              ? FALLBACK_POSTER
              : allowsVideo
                ? DESKTOP_START_POSTER
                : DESKTOP_END_POSTER
          }
          alt="Freeze-dried treats filling a branded Sub Zero Treats bucket in a black-cherry studio"
          fetchPriority="high"
          onError={() => setPosterFailed(true)}
        />
      </picture>

      {allowsVideo ? (
        <video
          ref={videoRef}
          className="lumina-portal__video"
          data-testid="lumina-hero-video"
          aria-hidden="true"
          muted
          playsInline
          preload="metadata"
          onCanPlay={() => setReady(true)}
          onEnded={() => {
            hasEndedRef.current = true;
          }}
          onError={() => setReady(false)}
        >
          <source
            media="(min-width: 701px)"
            src="/media/higgsfield/sweet-impact-master-desktop.webm?v=20260801-baked"
            type="video/webm"
          />
          <source
            media="(min-width: 701px)"
            src="/media/higgsfield/sweet-impact-master-desktop.mp4?v=20260801-baked"
            type="video/mp4"
          />
        </video>
      ) : null}
    </div>
  );
}

// Compatibility export for existing tests and downstream imports.
export const LuminaPortalVideo = CampaignImpactMedia;
